<template>
    <teleport to="body">
        <transition name="base-modal" @after-leave="$emit('after-leave')">
            <div v-if="visible" class="base-modal-overlay" :style="{zIndex}" @click.self="$emit('close')">
                <div class="base-modal-dialog" :class="contentClass" :style="dialogStyle">
                    <slot></slot>
                </div>
            </div>
        </transition>
    </teleport>
</template>

<script>
const _openModals = []

export default {
    name: 'BaseModal',
    props: {
        visible: { type: Boolean, required: true },
        width: { type: String, default: '440px' },
        maxWidth: { type: String, default: '90vw' },
        maxHeight: { type: String, default: '85vh' },
        zIndex: { type: Number, default: 200020 },
        contentClass: { type: [String, Object, Array], default: '' },
    },
    emits: ['close', 'after-leave'],
    computed: {
        dialogStyle() {
            return {
                width: this.width,
                maxWidth: this.maxWidth,
                maxHeight: this.maxHeight,
            }
        },
    },
    watch: {
        visible(val, oldVal) {
            if (val && !oldVal) {
                _openModals.push(this)
                this._prevBodyOverflow = document.body.style.overflow
                document.body.style.overflow = 'hidden'
            } else if (!val && oldVal) {
                const idx = _openModals.indexOf(this)
                if (idx !== -1) _openModals.splice(idx, 1)
                document.body.style.overflow = this._prevBodyOverflow ?? ''
            }
        },
    },
    mounted() {
        this._onKeydown = (e) => {
            if (e.key !== 'Escape' || !this.visible) return
            if (_openModals[_openModals.length - 1] !== this) return
            if (document.querySelector('.dtp-overlay')) return
            e.stopPropagation()
            this.$emit('close')
        }
        document.addEventListener('keydown', this._onKeydown, true)
    },
    beforeUnmount() {
        document.removeEventListener('keydown', this._onKeydown, true)
        if (this.visible) {
            const idx = _openModals.indexOf(this)
            if (idx !== -1) _openModals.splice(idx, 1)
            document.body.style.overflow = this._prevBodyOverflow ?? ''
        }
    },
}
</script>

<style>
.base-modal-overlay {
    position: fixed;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(0, 0, 0, 0.4);
    backdrop-filter: blur(4px);
    -webkit-backdrop-filter: blur(4px);

    --panel-bg: #ffffff;
    --panel-border: #e5e5ea;
    --panel-titlebar-bg: #f8f8f8;
    --panel-text-primary: #1c1c1e;
    --panel-text-secondary: #8e8e93;
    --panel-selected-bg: rgba(0, 122, 255, 0.08);
}

html.dark .base-modal-overlay {
    --panel-bg: #15171C;
    --panel-border: #26272F;
    --panel-titlebar-bg: #1E2027;
    --panel-text-primary: #F5F5F5;
    --panel-text-secondary: #9A9CAE;
    --panel-selected-bg: rgba(16, 126, 255, 0.15);
}

html.midnight .base-modal-overlay {
    --panel-bg: #1A1714;
    --panel-border: #2C2823;
    --panel-titlebar-bg: #241F1B;
    --panel-text-primary: #EAE6E1;
    --panel-text-secondary: #A9A39D;
    --panel-selected-bg: rgba(200, 134, 60, 0.15);
}

html.cream .base-modal-overlay {
    --panel-bg: #FEFCF8;
    --panel-border: #E8E3DA;
    --panel-titlebar-bg: #F5F1EA;
    --panel-text-primary: #3D3832;
    --panel-text-secondary: #847C72;
    --panel-selected-bg: rgba(5, 150, 105, 0.08);
}

.base-modal-dialog {
    background: var(--panel-bg);
    border-radius: 14px;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.25);
    overflow: hidden;
}

html.dark .base-modal-dialog {
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.06);
}

html.midnight .base-modal-dialog {
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.04);
}

/* Enter */
.base-modal-enter-active {
    transition: opacity 0.2s ease-out;
}
.base-modal-enter-active .base-modal-dialog {
    animation: baseModalDialogIn 0.25s ease-out;
}
.base-modal-enter-from {
    opacity: 0;
}

/* Leave */
.base-modal-leave-active {
    transition: opacity 0.15s ease-in;
}
.base-modal-leave-to {
    opacity: 0;
}

@keyframes baseModalDialogIn {
    from { opacity: 0; transform: scale(0.97) translateY(8px); }
    to { opacity: 1; transform: scale(1) translateY(0); }
}
</style>
